import {
  MODUL_PROJEKTI,
  nazivVira,
  potrebujeOtvoritevIzBilance,
  virZaLetoIzObdobij,
  type IzbiraZavihka,
  type LetoPodatkov,
  type OpozoriloUvoza,
  type OtvoritevLeta,
  type PredogledBrutoBilance,
  type PreslikavaPotrebna,
  type PreslikavaStolpcev as PreslikavaStolpcevTip,
  type ProgramPoLetih as ProgramPoLetihTip,
  type StanjeUvozaZaPrikaz,
} from '@koda/shared';
import { useCallback, useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api';
import { IzvirneDatoteke } from '../sestavni/IzvirneDatoteke';
import { MinimaxPovezava } from '../sestavni/MinimaxPovezava';
import { PrimerjavaUvoza } from '../sestavni/PrimerjavaUvoza';
import { OpozorilaUvoza } from '../sestavni/OpozorilaUvoza';
import { PreslikavaStolpcev } from '../sestavni/PreslikavaStolpcev';
import { ProgramPoLetih, ZnackaVira } from '../sestavni/ProgramPoLetih';
import { UjemanjeOtvoritve } from '../sestavni/UjemanjeOtvoritve';

const MESECI = ['januar', 'februar', 'marec', 'april', 'maj', 'junij', 'julij', 'avgust', 'september', 'oktober', 'november', 'december'];

/** Kaj piše v naslovu kartice glede na stanje uvoza. */
const NASLOV: Record<StanjeUvozaZaPrikaz['stanje'], string> = {
  'v-teku': 'Uvoz teče …',
  potrjen: 'Uvoz je potrjen',
  zavrnjen: 'Uvoz je zavrnjen',
  padel: 'Uvoz se je ustavil',
};

const RAZRED: Record<StanjeUvozaZaPrikaz['stanje'], string> = {
  'v-teku': '',
  potrjen: 'uspeh',
  zavrnjen: 'zavrnjeno',
  padel: 'zavrnjeno',
};

/** Datum v slovenskem zapisu (CLAUDE.md, pravilo 10). */
function datumSl(iso: string): string {
  return new Date(iso).toLocaleDateString('sl-SI', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

/**
 * Od kod je otvoritev leta in ali se drzi (docs/ARHITEKTURA.md, 5.4).
 *
 * Isto besedilo je v seznamu let in v razdelku otvoritve, da uporabnik
 * vidi isto stanje, kjer koli pogleda.
 */
export function opisOtvoritve(leto: number, o: OtvoritevLeta): string {
  if (o.nacin === 'bruto-bilanca') {
    return (
      `Otvoritev leta ${String(leto)} je iz bruto bilance` +
      `${o.datoteka === null ? '' : ` (${o.datoteka})`}, naložene ${datumSl(o.datum)}` +
      `${o.stanjeNaDan === null ? '; dan začetnega salda iz glave datoteke ni znan' : `; začetni saldo na dan ${datumSl(o.stanjeNaDan)}`}.`
    );
  }
  if (o.nacin === 'izracun') {
    const osnova = `Otvoritev leta ${String(leto)} je izračunana iz leta ${String(o.izLeta ?? leto - 1)} (${datumSl(o.datum)}).`;
    return o.zastarela
      ? `${osnova} Podatki leta ${String(o.izLeta ?? leto - 1)} so bili od takrat spremenjeni, zato izračun ne drži več: naloži bruto bilanco leta ${String(leto)} ali otvoritev izračunaj znova.`
      : osnova;
  }
  return `Otvoritev leta ${String(leto)} je iz časa, ko se izvor še ni zapisoval (${datumSl(o.datum)}). Naloži bruto bilanco tega leta, da bo izvor znan.`;
}

/** Nalaganje izvoza glavne knjige. */
export function Uvoz(): JSX.Element {
  const { id = '' } = useParams();
  const [nazivPodjetja, setNazivPodjetja] = useState('');
  // Vir podjetja od zacetka; vir za izbrano leto pove »Program po letih« (5.2).
  const [virPodjetja, setVirPodjetja] = useState('pantheon');
  const [program, setProgram] = useState<ProgramPoLetihTip | null>(null);
  const [leto, setLeto] = useState(String(new Date().getFullYear()));
  const vir =
    (program === null ? undefined : virZaLetoIzObdobij(program.obdobja, Number.parseInt(leto, 10))) ?? virPodjetja;
  const [datoteka, setDatoteka] = useState<File | null>(null);
  const [nalaga, setNalaga] = useState(false);
  const [stanje, setStanje] = useState<StanjeUvozaZaPrikaz | null>(null);
  const [izbira, setIzbira] = useState<IzbiraZavihka | null>(null);
  const [zavihek, setZavihek] = useState('');
  const [napaka, setNapaka] = useState<string | null>(null);
  // Uvoz obveznega stolpca ni prepoznal; admin ga poveze sam.
  const [preslikava, setPreslikava] = useState<PreslikavaPotrebna | null>(null);
  // Preslikava, ki je ze shranjena pri podjetju; vidna je, da admin ve, po
  // cem se stolpci iscejo, in jo lahko odstrani, ko stranka izvoz popravi.
  const [shranjena, setShranjena] = useState<PreslikavaStolpcevTip | null>(null);
  // Pokritost uvoza po letih (do katerega meseca so podatki popolni); doloci
  // se ob uvozu, skrbnik jo tu lahko popravi (docs/ARHITEKTURA.md, 5.1).
  const [letaPodatkov, setLetaPodatkov] = useState<LetoPodatkov[]>([]);
  const [napakaPokritosti, setNapakaPokritosti] = useState<string | null>(null);
  const osveziLeta = useCallback((): void => {
    api
      .leta(id)
      .then(setLetaPodatkov)
      .catch(() => {
        setLetaPodatkov([]);
      });
  }, [id]);
  useEffect(osveziLeta, [osveziLeta]);
  const popraviPokritost = (leto: number, do_: number): void => {
    setNapakaPokritosti(null);
    api
      .popraviPokritost(id, leto, do_)
      .then(osveziLeta)
      .catch((e: unknown) => {
        setNapakaPokritosti(e instanceof Error ? e.message : 'Pokritosti ni bilo mogoče shraniti.');
      });
  };

  // Preslikava se bere za vir leta, ki je vpisano v obrazcu (5.2): pri
  // podjetju, ki je zamenjalo program, ima starejse leto druga polja.
  const letoStevilka = Number.parseInt(leto, 10);
  const osveziPreslikavo = useCallback((): void => {
    api
      .preslikavaStolpcev(id, Number.isFinite(letoStevilka) ? letoStevilka : undefined)
      .then(setShranjena)
      .catch(() => {
        setShranjena(null);
      });
  }, [id, letoStevilka]);

  useEffect(osveziPreslikavo, [osveziPreslikavo]);

  useEffect(() => {
    api
      .programPoLetih(id)
      .then(setProgram)
      .catch(() => {
        setProgram(null);
      });
  }, [id]);

  // Sifrant projektov je samo pri podjetju z vklopljenim modulom Projekti (15).
  const [moduli, setModuli] = useState<string[]>([]);
  useEffect(() => {
    api
      .moduli(id)
      .then(setModuli)
      .catch(() => {
        setModuli([]);
      });
  }, [id]);

  // Otvoritev je loceno nalaganje in ima svoje stanje; pri Pantheonu in
  // Minimaxu je v izvozu glavne knjige in tega razdelka ni.
  const [bilanca, setBilanca] = useState<File | null>(null);
  // Leto otvoritve je lastna izbira, ne leto iz obrazca za glavno knjigo:
  // bruto bilanca se je nekoc po pomoti pripela k napacnemu letu (5.4).
  // Predlaga ga predogled iz glave datoteke; brez glave skrbnik leto potrdi.
  const [letoOtvoritve, setLetoOtvoritve] = useState(String(new Date().getFullYear()));
  const [predogled, setPredogled] = useState<PredogledBrutoBilance | null>(null);
  const [predogledTece, setPredogledTece] = useState(false);
  const [letoPotrjeno, setLetoPotrjeno] = useState(false);
  const [otvoritevTece, setOtvoritevTece] = useState(false);
  const [izidOtvoritve, setIzidOtvoritve] = useState<{
    kontov: number;
    potrjen: boolean;
    opozorila: OpozoriloUvoza[];
    izid?: string;
    kontoIzida?: string;
    izLeta?: number;
  } | null>(null);
  const [napakaOtvoritve, setNapakaOtvoritve] = useState<string | null>(null);

  const izberiBilanco = (datoteka: File | null): void => {
    setBilanca(datoteka);
    setIzidOtvoritve(null);
    setPredogled(null);
    setLetoPotrjeno(false);
    setNapakaOtvoritve(null);
    if (!datoteka) return;
    setPredogledTece(true);
    api
      .predogledOtvoritve(id, datoteka)
      .then((p) => {
        setPredogled(p);
        // Leto iz glave datoteke ima prednost pred vsako prejsnjo izbiro.
        if (p.obdobje?.letoOtvoritve != null) setLetoOtvoritve(String(p.obdobje.letoOtvoritve));
      })
      .catch((e: unknown) => {
        setNapakaOtvoritve(e instanceof Error ? e.message : 'Datoteke ni bilo mogoče prebrati.');
      })
      .finally(() => {
        setPredogledTece(false);
      });
  };

  const letoIzDatoteke = predogled?.obdobje?.letoOtvoritve ?? null;
  const letoSeUjema = letoIzDatoteke === null || String(letoIzDatoteke) === letoOtvoritve;
  const letoNepreverjeno = predogled !== null && predogled.obdobje === null;

  const posljiOtvoritev = (dogodek: FormEvent): void => {
    dogodek.preventDefault();
    if (!bilanca) return;
    setOtvoritevTece(true);
    setNapakaOtvoritve(null);
    setIzidOtvoritve(null);
    api
      .otvoritev(id, Number.parseInt(letoOtvoritve, 10), bilanca, letoNepreverjeno && letoPotrjeno)
      .then((izid) => {
        setIzidOtvoritve(izid);
        osveziLeta();
      })
      .catch((e: unknown) => {
        setNapakaOtvoritve(e instanceof Error ? e.message : 'Otvoritve ni bilo mogoče naložiti.');
      })
      .finally(() => {
        setOtvoritevTece(false);
      });
  };

  // Veljavna otvoritev izbranega leta: od kod je in ali se drzi
  // (docs/ARHITEKTURA.md, 5.4). Po njej stran pove, kaj je nalozeno, in
  // izracun ne ponudi, kadar ima leto bruto bilanco.
  const otvoritevLeta = letaPodatkov.find((l) => String(l.leto) === leto)?.otvoritev ?? null;

  /**
   * Izracuna otvoritev iz preteklega leta.
   *
   * Samo za leto brez bruto bilance: bruto bilanca ima prednost in je
   * streznik z izracunom ne prepise. Prejsnji izracun istega leta se
   * nadomesti, zato se najprej potrdi.
   */
  const izracunajOtvoritev = (): void => {
    const izbrano = Number.parseInt(leto, 10);
    if (!Number.isFinite(izbrano)) return;
    const potrjeno = window.confirm(
      `Otvoritev leta ${String(izbrano)} bo izračunana iz leta ${String(izbrano - 1)}: ` +
        `otvoritev leta ${String(izbrano - 1)} + promet leta ${String(izbrano - 1)}, izid leta ` +
        'pa na konto za prenos izida. Izračun je nadomestilo za bruto bilanco; če jo imaš, ' +
        'raje naloži njo. Nadaljujem?',
    );
    if (!potrjeno) return;
    setOtvoritevTece(true);
    setNapakaOtvoritve(null);
    setIzidOtvoritve(null);
    api
      .izracunajOtvoritev(id, izbrano)
      .then((izid) => {
        setIzidOtvoritve(izid);
        osveziLeta();
      })
      .catch((e: unknown) => {
        setNapakaOtvoritve(e instanceof Error ? e.message : 'Otvoritve ni bilo mogoče izračunati.');
      })
      .finally(() => {
        setOtvoritevTece(false);
      });
  };

  useEffect(() => {
    api
      .podjetja()
      .then((vsa) => {
        const p = vsa.find((x) => x.id === id);
        if (p) {
          setNazivPodjetja(p.naziv);
          setVirPodjetja(p.vir);
        }
      })
      .catch(() => {
        /* naziv ni nujen za delovanje obrazca */
      });
  }, [id]);

  // Poizvedovanje stanja ustavimo, ko uporabnik zapusti stran.
  const ura = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (ura.current !== null) window.clearTimeout(ura.current);
    },
    [],
  );

  /**
   * Poizveduje stanje uvoza, dokler obdelava tece.
   *
   * Velik izvoz se obdeluje dlje, kot sme trajati zahteva, zato strežnik
   * zahtevo zaključi takoj, tu pa se izpisuje napredek.
   */
  const spremljaj = useCallback(
    (uvoz: string): void => {
      api
        .stanjeUvoza(id, uvoz)
        .then((novo) => {
          setStanje(novo);
          if (novo.stanje === 'v-teku') {
            ura.current = window.setTimeout(() => {
              spremljaj(uvoz);
            }, 1500);
            return;
          }
          setNalaga(false);
          // Nov uvoz doloci pokritost na novo.
          if (novo.stanje === 'potrjen') osveziLeta();
        })
        .catch((e: unknown) => {
          setNapaka(e instanceof Error ? e.message : 'Stanja uvoza ni bilo mogoče prebrati.');
          setNalaga(false);
        });
    },
    [id, osveziLeta],
  );

  /**
   * Naloži datoteko; `izbran` je zavihek, kadar ga je uporabnik že izbral.
   *
   * Datoteka ostane izbrana v obrazcu, zato jo brskalnik ob izbiri zavihka
   * pošlje še enkrat sam — uporabniku je ni treba znova poiskati.
   */
  const naloziDatoteko = (izbran?: string): void => {
    if (!datoteka) return;
    setNalaga(true);
    setStanje(null);
    setNapaka(null);
    api
      .uvoz(id, Number.parseInt(leto, 10), vir, datoteka, izbran)
      .then((odgovor) => {
        if ('zavihki' in odgovor) {
          setIzbira(odgovor);
          setZavihek(odgovor.zavihki[0] ?? '');
          setNalaga(false);
          return;
        }
        // Stolpcev ne poznamo: namesto napake ponudimo povezovanje stolpcev.
        if ('preslikavaPotrebna' in odgovor) {
          setPreslikava(odgovor.preslikavaPotrebna);
          setIzbira(null);
          setNalaga(false);
          return;
        }
        setPreslikava(null);
        setIzbira(null);
        setStanje({ uvoz: odgovor.uvoz, stanje: 'v-teku', knjizb: 0, opozorila: [], napaka: null });
        spremljaj(odgovor.uvoz);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Uvoz ni uspel.');
        setNalaga(false);
      });
  };

  const poslji = (dogodek: FormEvent): void => {
    dogodek.preventDefault();
    setIzbira(null);
    setPreslikava(null);
    naloziDatoteko();
  };

  return (
    <main>
      <nav className="drobtine">
        <Link to="/">Podjetja</Link>
        <Link to={`/podjetje/${id}`}>{nazivPodjetja || 'Podjetje'}</Link>
      </nav>
      <h1>Nalaganje glavne knjige</h1>
      <p className="podnaslov">
        Naloži surov izvoz glavne knjige v obliki CSV ali Excel (.xlsx). Nov uvoz nadomesti
        knjižbe istega leta; druga leta ostanejo nedotaknjena.
      </p>
      {potrebujeOtvoritevIzBilance(vir) && (
        <p className="namig">
          Pri {nazivVira(vir) ?? vir} za <strong>zaključeno leto</strong> izvoz z datumskim filtrom do 31. 12.
          ne zajame knjižb z zaključnim datumom (zapiranje izkaza uspeha, davek od dohodka); otvoritev
          naslednjega leta jih ima, zato se ujemanje otvoritve ne izide. Izvozi z datumskim filtrom do{' '}
          <code>Z31.12.LLLL</code> (angleški BC <code>C31.12.LLLL</code>). Zaključne knjižbe dobijo
          vrsto temeljnice iz <em>Šifre izvora</em> (npr. <code>ZAPUSPEHA</code>); v Nastavitvah
          razvrščanja ji nastavi vlogo <strong>zapiranje leta (izkaz)</strong>, da izkaz ostane pravilen.
        </p>
      )}

      <section className="kartica">
        <form onSubmit={poslji} className="obrazec">
          <label>
            Poslovno leto
            <input
              type="number"
              value={leto}
              min={1990}
              max={2999}
              onChange={(d) => {
                setLeto(d.target.value);
              }}
              required
            />
          </label>
          <p className="drobno vir-leta">
            Vir za leto {leto}: <ZnackaVira vir={vir} />
            <span className="namig">
              {' '}
              Vir je določen v kartici »Program po letih« in se ob nalaganju ne spreminja; naloži
              izvoz iz tega programa.
            </span>
          </p>
          <label>
            Datoteka (CSV ali .xlsx)
            <input
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(d) => {
                setDatoteka(d.target.files?.[0] ?? null);
                setIzbira(null);
                setPreslikava(null);
              }}
              required
            />
          </label>
          <button type="submit" disabled={nalaga || !datoteka}>
            {nalaga ? 'Uvažam … (lahko traja minuto)' : 'Naloži'}
          </button>
        </form>
      </section>

      {napaka !== null && <p className="napaka">{napaka}</p>}

      {preslikava !== null && (
        <PreslikavaStolpcev
          podjetje={id}
          leto={Number.parseInt(leto, 10)}
          potrebna={preslikava}
          naShranjeno={() => {
            setPreslikava(null);
            osveziPreslikavo();
            naloziDatoteko();
          }}
          naPreklic={() => {
            setPreslikava(null);
          }}
        />
      )}

      {shranjena !== null && Object.keys(shranjena.preslikava).length > 0 && preslikava === null && (
        <section className="kartica">
          <h2>Preslikava stolpcev</h2>
          <p className="podnaslov">
            Pri tem podjetju so stolpci povezani ročno; uvoz jih tako išče sam. Če je stranka
            izvoz popravila, preslikavo odstrani — takrat spet veljajo znana imena stolpcev.
          </p>
          <ul className="seznam">
            {shranjena.polja
              .filter((p) => shranjena.preslikava[p.polje] !== undefined)
              .map((p) => (
                <li key={p.polje}>
                  <span>{p.ime}</span>
                  <span className="drobno">{shranjena.preslikava[p.polje]}</span>
                </li>
              ))}
          </ul>
          <button
            type="button"
            className="tiho"
            onClick={() => {
              if (!window.confirm('Odstranim ročno preslikavo stolpcev za to podjetje?')) return;
              api
                .shraniPreslikavoStolpcev(id, {}, Number.isFinite(letoStevilka) ? letoStevilka : undefined)
                .then(setShranjena)
                .catch((e: unknown) => {
                  setNapaka(e instanceof Error ? e.message : 'Preslikave ni bilo mogoče odstraniti.');
                });
            }}
          >
            Odstrani preslikavo
          </button>
        </section>
      )}

      {program !== null && (
        <ProgramPoLetih
          podjetje={id}
          program={program}
          letaSPodatki={letaPodatkov.map((l) => l.leto)}
          naSpremembo={(novo) => {
            setProgram(novo);
            osveziLeta();
          }}
        />
      )}

      {moduli.includes(MODUL_PROJEKTI) && (
        <section className="kartica">
          <h2>Projekti</h2>
          <p>
            Šifrant projektov, register osnovnih sredstev, temeljnica in zaključek meseca so na strani{' '}
            <Link to={`/podjetje/${id}/projekti/nastavitve`}>Nastavitve projektov</Link>.
          </p>
        </section>
      )}

      {letaPodatkov.length > 0 && (
        <section className="kartica">
          <h2>Pokritost podatkov</h2>
          <p className="podnaslov">
            Do katerega meseca so podatki leta popolni. Določi se ob vsakem uvozu glavne knjige
            (zadnji mesec s knjižbo; otvoritev se ne šteje); tu jo lahko popraviš, npr. če je v
            datoteki že nekaj avgustovskih knjižb, popolni pa so podatki do julija. Po njej se
            ravnajo Primerjava, Pregled kazalnikov in primerjalna obdobja formul.
          </p>
          <ul className="seznam-let">
            {letaPodatkov.map((l) => (
              <li key={l.leto}>
                <span>
                  <strong>{l.leto}</strong> <ZnackaVira vir={l.vir} />{' '}
                  <span className="drobno">{l.knjizb.toLocaleString('sl-SI')} knjižb</span>
                </span>
                {l.otvoritev !== null && (
                  <span className={l.otvoritev.zastarela ? 'opozorilo' : 'drobno'}>
                    {opisOtvoritve(l.leto, l.otvoritev)}
                  </span>
                )}
                {l.pokritost === null ? (
                  <span className="drobno">brez potrjenega uvoza glavne knjige</span>
                ) : (
                  <label>
                    podatki do{' '}
                    <select
                      value={l.pokritost.do}
                      onChange={(d) => {
                        popraviPokritost(l.leto, Number.parseInt(d.target.value, 10));
                      }}
                    >
                      <option value={0}>samo otvoritev</option>
                      {MESECI.map((ime, i) => (
                        <option key={ime} value={i + 1}>
                          {ime}
                        </option>
                      ))}
                    </select>{' '}
                    <span className="drobno">
                      {l.pokritost.vir === 'rocno' ? 'popravil skrbnik' : 'določeno ob uvozu'}
                    </span>
                  </label>
                )}
              </li>
            ))}
          </ul>
          {napakaPokritosti !== null && <p className="napaka">{napakaPokritosti}</p>}
        </section>
      )}

      {letaPodatkov.length > 1 && (
        <UjemanjeOtvoritve
          podjetje={id}
          leta={letaPodatkov.map((l) => l.leto)}
          // Seznam let se prebere znova po vsakem uvozu (glavna knjiga, bruto
          // bilanca, izracun otvoritve); sprememba knjizb ali otvoritve v njem
          // sprozi ponovno preverjanje.
          osvezitev={letaPodatkov.map((l) => `${String(l.leto)}:${String(l.knjizb)}:${l.otvoritev?.datum ?? ''}`).join('|')}
        />
      )}

      {potrebujeOtvoritevIzBilance(vir) && (
        <section className="kartica">
          <h2>Otvoritev (bruto bilanca)</h2>
          <p className="podnaslov">
            {nazivVira(vir) ?? vir} otvoritve ne izvaža — izvoz glavne knjige vsebuje samo promet
            leta. Dokler otvoritev ni naložena, bilanca stanja prikazuje spremembe v letu in ne
            stanj. Otvoritev leta pride iz <strong>bruto bilance tega leta</strong>; samo kadar je
            ni, se lahko izračuna iz preteklega leta. Bruto bilanca ima vedno prednost: naložena
            nadomesti izračun, izračun pa naložene bruto bilance ne prepiše.
          </p>
          <ul className="drobno">
            {letaPodatkov.map((l) => (
              <li key={l.leto} className={l.otvoritev?.zastarela ? 'opozorilo' : undefined}>
                {l.otvoritev === null ? `Leto ${String(l.leto)} še nima otvoritve.` : opisOtvoritve(l.leto, l.otvoritev)}
              </li>
            ))}
          </ul>
          <p className="namig">
            V programu izvozi <strong>bruto bilanco za leto, ki mu otvoritev pripada</strong>, z
            datumskim filtrom 1. 1. – 31. 12. tega leta — izberi <em>»Pošlji v …«</em> in nato{' '}
            <em>»Microsoft Excelov dokument (podatki in postavitev)«</em>. Uporabijo se stolpci{' '}
            <strong>začetnega salda</strong>, to je stanje na 31. 12. preteklega leta, ki postane
            otvoritev izbranega leta; konec filtra ni pomemben. Portal leto prebere iz glave
            datoteke in ga predlaga; če ga ne more prebrati, ga potrdiš sam. Vsako leto ima svojo
            otvoritev, neodvisno od drugih let in od leta v obrazcu za glavno knjigo zgoraj.
          </p>
          <form onSubmit={posljiOtvoritev} className="obrazec">
            <label>
              Bruto bilanca (.xlsx)
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(d) => {
                  izberiBilanco(d.target.files?.[0] ?? null);
                }}
                required
              />
            </label>
            {predogledTece && <p className="drobno">Berem glavo datoteke …</p>}
            {predogled !== null && predogled.obdobje !== null && (
              <p className={letoSeUjema ? 'uspeh-besedilo' : 'opozorilo'}>
                V datoteki: datumski filter {predogled.obdobje.filter}, začetni saldo na dan{' '}
                {datumSl(predogled.obdobje.stanjeNaDan)}
                {predogled.obdobje.letoOtvoritve === null
                  ? ' — to ni 31. 12., zato ni otvoritev nobenega leta (stanje sredi leta).'
                  : ` → otvoritev leta ${String(predogled.obdobje.letoOtvoritve)}.`}{' '}
                Kontov: {predogled.kontov.toLocaleString('sl-SI')}
                {predogled.razlika !== '0.00' && `, otvoritev se ne izravna (razlika ${predogled.razlika})`}.
                {!letoSeUjema && ` Izbrano leto ${letoOtvoritve} se s tem ne ujema; portal bo nalaganje zavrnil.`}
              </p>
            )}
            {letoNepreverjeno && (
              <p className="opozorilo">
                V glavi datoteke ni datumskega filtra, zato portal ne more preveriti, kateremu letu
                otvoritev pripada. Preveri sam in potrdi spodaj; bruto bilanca napačnega leta bi
                dala stanja, napačna za celo leto prometa.
              </p>
            )}
            <label>
              Leto otvoritve
              <input
                type="number"
                aria-label="Leto otvoritve"
                value={letoOtvoritve}
                min={1990}
                max={2999}
                onChange={(d) => {
                  setLetoOtvoritve(d.target.value);
                  setLetoPotrjeno(false);
                }}
                required
              />
              <span className="drobno">
                {letoIzDatoteke !== null
                  ? ` predlagano iz datoteke: ${String(letoIzDatoteke)}`
                  : ' vpiši leto, ki mu otvoritev pripada (tudi starejše leto brez podatkov)'}
              </span>
            </label>
            {letoNepreverjeno && (
              <label className="potrditev">
                <input
                  type="checkbox"
                  checked={letoPotrjeno}
                  onChange={(d) => {
                    setLetoPotrjeno(d.target.checked);
                  }}
                />{' '}
                Potrjujem, da je to bruto bilanca za leto {letoOtvoritve} (začetni saldo na dan 31. 12.{' '}
                {String(Number.parseInt(letoOtvoritve, 10) - 1)}).
              </label>
            )}
            <button
              type="submit"
              disabled={otvoritevTece || predogledTece || !bilanca || !letoSeUjema || (letoNepreverjeno && !letoPotrjeno)}
            >
              {otvoritevTece ? 'Nalagam …' : `Naloži otvoritev leta ${letoOtvoritve}`}
            </button>
          </form>

          <h3>Leto brez bruto bilance</h3>
          <p className="namig">
            Če bruto bilance za leto {leto} ni, se otvoritev lahko izračuna kot{' '}
            <strong>
              otvoritev leta {String(Number.parseInt(leto, 10) - 1)} + promet leta{' '}
              {String(Number.parseInt(leto, 10) - 1)}
            </strong>
            . Konti izkaza poslovnega izida se ne prenesejo — njihov skupni saldo je izid
            preteklega leta in gre na konto za prenos izida (nastavi ga v Nastavitvah razvrščanja;
            privzeto 930). Pred izračunom morata biti naložena otvoritev in glavna knjiga leta{' '}
            {String(Number.parseInt(leto, 10) - 1)}. Izračun ne pozna zaključnih knjižb programa
            (davek od dohodka, prenosi ob zaključku leta), zato je bruto bilanca natančnejša. Če se
            podatki leta {String(Number.parseInt(leto, 10) - 1)} pozneje spremenijo, izračun ne
            drži več — stran in poročila to pokažejo, otvoritev pa izračunaš znova ali naložiš
            bruto bilanco.
          </p>
          {otvoritevLeta?.nacin === 'bruto-bilanca' ? (
            <p className="drobno">
              Leto {leto} ima otvoritev iz bruto bilance, zato izračun ni na voljo. Napačno bruto
              bilanco nadomestiš tako, da naložiš pravo.
            </p>
          ) : (
            <button type="button" className="gumb" onClick={izracunajOtvoritev} disabled={otvoritevTece}>
              {otvoritevTece
                ? 'Računam …'
                : `Izračunaj otvoritev leta ${leto} iz leta ${String(Number.parseInt(leto, 10) - 1)}`}
            </button>
          )}

          {napakaOtvoritve !== null && <p className="napaka">{napakaOtvoritve}</p>}

          {izidOtvoritve !== null && (
            <div className={izidOtvoritve.potrjen ? 'kartica uspeh' : 'kartica zavrnjeno'}>
              <p>
                {izidOtvoritve.potrjen ? 'Otvoritev je naložena' : 'Otvoritev je zavrnjena'} —
                kontov: <strong>{izidOtvoritve.kontov.toLocaleString('sl-SI')}</strong>
              </p>
              {izidOtvoritve.izid !== undefined && (
                <p>
                  Izid leta {String(izidOtvoritve.izLeta ?? '')}:{' '}
                  <strong>
                    {Number(izidOtvoritve.izid).toLocaleString('sl-SI', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    {' €'}
                  </strong>{' '}
                  na konto <strong>{izidOtvoritve.kontoIzida}</strong>
                </p>
              )}
              {izidOtvoritve.opozorila.length > 0 && (
                <ul className="opozorila">
                  {izidOtvoritve.opozorila.map((o) => (
                    <li key={o.vrsta} className={o.napaka ? 'napaka-vrstica' : ''}>
                      <strong>{o.napaka ? 'Napaka' : 'Opozorilo'}:</strong> {o.sporocilo}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      {izbira !== null && (
        <section className="kartica">
          <h2>Izberi zavihek</h2>
          <p>{izbira.sporocilo}</p>
          <div className="obrazec">
            <label>
              Zavihek z glavno knjigo
              <select
                value={zavihek}
                onChange={(d) => {
                  setZavihek(d.target.value);
                }}
              >
                {izbira.zavihki.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={nalaga || zavihek === ''}
              onClick={() => {
                naloziDatoteko(zavihek);
              }}
            >
              {nalaga ? 'Uvažam …' : 'Naloži izbrani zavihek'}
            </button>
          </div>
        </section>
      )}

      {stanje !== null && (
        <section className={`kartica ${RAZRED[stanje.stanje]}`}>
          <h2>{NASLOV[stanje.stanje]}</h2>
          <p>
            {stanje.stanje === 'v-teku' ? 'Zapisanih knjižb' : 'Prebranih knjižb'}:{' '}
            <strong>{stanje.knjizb.toLocaleString('sl-SI')}</strong>
          </p>
          {stanje.stanje === 'v-teku' && (
            <p className="namig">
              Obdelava teče na strežniku; stran lahko pustiš odprto. Pri velikem izvozu traja
              nekaj minut.
            </p>
          )}
          {stanje.napaka !== null && <p className="napaka">{stanje.napaka}</p>}
          {stanje.stanje !== 'v-teku' && stanje.opozorila.length === 0 && (
            <p>Kontrole niso javile ničesar.</p>
          )}
          <OpozorilaUvoza opozorila={stanje.opozorila} />
          {stanje.primerjava !== undefined && stanje.primerjava !== null && (
            <PrimerjavaUvoza primerjava={stanje.primerjava} />
          )}
          {stanje.stanje === 'potrjen' && (
            <p>
              <Link to={`/podjetje/${id}`}>Odpri poročila</Link>
            </p>
          )}
        </section>
      )}

      {vir === 'minimax' && (
        <MinimaxPovezava
          podjetje={id}
          leto={Number.parseInt(leto, 10) || new Date().getFullYear()}
          naUvoz={(uvoz) => {
            // Uvoz prek API tece v ozadju kot uvoz datoteke: isto spremljanje, ista kartica stanja.
            setIzbira(null);
            setPreslikava(null);
            setNapaka(null);
            setNalaga(true);
            setStanje({ uvoz, stanje: 'v-teku', knjizb: 0, opozorila: [], napaka: null });
            spremljaj(uvoz);
          }}
        />
      )}

      <IzvirneDatoteke
        podjetje={id}
        osvezitev={`${stanje?.stanje ?? ''}|${izidOtvoritve === null ? '' : String(izidOtvoritve.potrjen)}`}
      />
    </main>
  );
}
